import React, { useState, useEffect } from 'react';
import { useBusinessStore } from '@/stores/useBusinessStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Loader2, Upload, X } from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const serviceSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100),
    description: z.string().max(500).optional(),
    category: z.string().min(1, 'Category is required'),
    price: z.string().min(1, 'Price is required'),
    duration: z.string().min(1, 'Duration is required'),
    delivery_mode: z.string().min(1, 'Delivery mode is required'),
    is_active: z.boolean(),
});

type ServiceFormData = z.infer<typeof serviceSchema>;

export const ServiceForm: React.FC = () => {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const { activeBusiness, createService, updateService, services, loading } = useBusinessStore();
    const isEditMode = !!serviceId;
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    const { register, handleSubmit, formState: { errors }, reset } = useForm<ServiceFormData>({
        resolver: zodResolver(serviceSchema),
        defaultValues: { is_active: true, category: 'General', delivery_mode: 'in_person' },
    });

    useEffect(() => {
        if (isEditMode && services.length > 0) {
            const service = services.find((s) => s.id === serviceId);
            if (service) {
                reset({
                    name: service.name,
                    description: service.description,
                    category: service.category,
                    price: String(service.price),
                    duration: String(service.duration),
                    delivery_mode: service.delivery_mode,
                    is_active: service.is_active,
                });
                if (service.media_urls?.length) setPreviewUrls(service.media_urls);
            }
        }
    }, [isEditMode, services, serviceId, reset]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newPreviews = Array.from(e.target.files).map((f) => URL.createObjectURL(f));
            setPreviewUrls((prev) => [...prev, ...newPreviews]);
        }
    };

    const removeImage = (index: number) => {
        setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (data: ServiceFormData) => {
        if (!activeBusiness) return;
        try {
            const payload = {
                name: data.name,
                description: data.description || '',
                category: data.category,
                price: parseFloat(data.price),
                duration: parseInt(data.duration),
                delivery_mode: data.delivery_mode,
                is_active: data.is_active,
                media_urls: previewUrls,
            };
            if (isEditMode && serviceId) {
                await updateService(activeBusiness.id, serviceId, payload);
            } else {
                await createService(activeBusiness.id, payload);
            }
            navigate('/business/services');
        } catch (error) {
            console.error('Failed to save service:', error);
        }
    };

    const inputClass = "w-full px-4 py-2.5 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";
    const labelClass = "block text-sm font-medium text-foreground mb-1.5";

    return (
        <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate('/business/services')} className="p-2 rounded-lg hover:bg-muted transition-colors">
                    <ArrowLeft size={22} className="text-muted-foreground" />
                </button>
                <h1 className="text-2xl font-bold text-foreground">{isEditMode ? 'Edit Service' : 'New Service'}</h1>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="bg-card p-6 rounded-xl border border-border space-y-4">
                    <h2 className="text-lg font-semibold text-foreground">Service Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Service Name</label>
                            <input {...register('name')} className={inputClass} placeholder="e.g., Haircut" />
                            {errors.name && <p className="text-destructive text-xs mt-1">{errors.name.message}</p>}
                        </div>
                        <div>
                            <label className={labelClass}>Category</label>
                            <select {...register('category')} className={inputClass}>
                                <option value="General">General</option>
                                <option value="Beauty">Beauty</option>
                                <option value="Consulting">Consulting</option>
                                <option value="Cleaning">Cleaning</option>
                                <option value="Repair">Repair</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Description</label>
                        <textarea {...register('description')} rows={3} className={inputClass} placeholder="Service description..." />
                    </div>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border space-y-4">
                    <h2 className="text-lg font-semibold text-foreground">Pricing & Delivery</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelClass}>Price (KES)</label>
                            <input type="number" {...register('price')} className={inputClass} placeholder="0.00" />
                            {errors.price && <p className="text-destructive text-xs mt-1">{errors.price.message}</p>}
                        </div>
                        <div>
                            <label className={labelClass}>Duration (mins)</label>
                            <input type="number" {...register('duration')} className={inputClass} placeholder="60" />
                            {errors.duration && <p className="text-destructive text-xs mt-1">{errors.duration.message}</p>}
                        </div>
                        <div>
                            <label className={labelClass}>Delivery Mode</label>
                            <select {...register('delivery_mode')} className={inputClass}>
                                <option value="in_person">At My Place / In-person</option>
                                <option value="online">Online</option>
                                <option value="hybrid">Hybrid</option>
                                <option value="mobile">I Travel to Client</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <input type="checkbox" {...register('is_active')} id="svc_active" className="w-4 h-4 rounded border-border text-primary focus:ring-ring" />
                        <label htmlFor="svc_active" className="text-sm text-foreground">Service is active</label>
                    </div>
                </div>

                <div className="bg-card p-6 rounded-xl border border-border space-y-4">
                    <h2 className="text-lg font-semibold text-foreground">Images</h2>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {previewUrls.map((url, index) => (
                            <div key={index} className="relative aspect-square bg-muted rounded-lg overflow-hidden group">
                                <img src={url} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImage(index)} className="absolute top-1.5 right-1.5 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                        <label className="flex flex-col items-center justify-center aspect-square bg-muted/50 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                            <Upload size={20} className="text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">Add Image</span>
                            <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageChange} />
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => navigate('/business/services')} className="px-5 py-2.5 border border-border rounded-lg text-foreground font-medium hover:bg-muted transition-colors">
                        Cancel
                    </button>
                    <button type="submit" disabled={loading} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-opacity">
                        {loading && <Loader2 size={16} className="animate-spin" />}
                        {isEditMode ? 'Update Service' : 'Create Service'}
                    </button>
                </div>
            </form>
        </div>
    );
};
